import React from 'react';

import ShadowedList from '../../../../ShadowedList';
import { WidgetWrapper } from '../MetricSlideoutWizard/styles';
import { LabelDiv } from '../../../../../screens/Boards/PerformanceBoardForm';
import Typography from '../../../../../kingpin/atoms/Typography';
import Inputs from '../../../../Inputs';
import WidgetFooter from '../../WidgetFooter';
import DragAndDropList from '../../../../DragAndDropList';
import Card from '../../../../../kingpin/atoms/Card';
import DragHandle from '../../../../DragAndDropList/DragHandle';
import Row from '../../../../Common/Row';
import Button from '../../../../../kingpin/atoms/Button';
import Dropdown from '../../../../Inputs/Dropdown';
import WarningBanner from '../../../../WarningBanner';

type EnrichedGroup =
  PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsDatasetGroup & {
    fieldOptions: DropdownOption[];
  };

const DatasetFieldsSection = ({
  group,
  isMultiple,
  onHeadingChanged,
  removeField,
  setFieldsOrder,
}: {
  group: EnrichedGroup;
  isMultiple: boolean;
  onHeadingChanged: (dataType: string, heading: string) => void;
  removeField: (dataType: string, field: string) => void;
  setFieldsOrder: (dataType: string, fields: string[]) => void;
}) => (
  <div style={{ marginBottom: 24 }}>
    {isMultiple && (
      <div style={{ marginBottom: 8 }}>
        <LabelDiv>
          <Typography.Body type="Label">Section Heading</Typography.Body>
        </LabelDiv>
        <Inputs.TextInput
          value={group.heading ?? ''}
          placeholder="Enter Heading"
          onChange={(e) => onHeadingChanged(group.dataType, e.target.value)}
        />
      </div>
    )}
    <LabelDiv>
      <Typography.Body type="Label">Fields</Typography.Body>
    </LabelDiv>
    <div style={{ marginBottom: 8 }}>
      <DragAndDropList
        items={group.fields}
        onOrderChanged={(fields) => setFieldsOrder(group.dataType, fields)}
        droppableId={`fields-order-${group.dataType}`}
        renderItem={(field) => (
          <Card key={field} style={{ padding: 8, marginBottom: 4 }}>
            <Row centerAlign spaceBetween>
              <Row centerAlign>
                <DragHandle />
                <Typography.Body type={'Button Text'}>{field}</Typography.Body>
              </Row>
              <Button
                size={'Small'}
                type={'Secondary'}
                icon="cross"
                onClick={() => removeField(group.dataType, field)}
              />
            </Row>
          </Card>
        )}
      />
    </div>
    <Row centerAlign spaceBetween>
      <div />
      <Dropdown options={group.fieldOptions} placeholder="Add Field" />
    </Row>
  </div>
);

const RelatedMetricDocumentsSlideoutWizard = ({
  title,
  onTitleChanged,
  metricOptions,
  selectedMetricLabel,
  hasAssignmentSelected,
  showDatePeriodAlert,
  isLoadingAssignments,
  isMultiple,
  groups,
  onHeadingChanged,
  removeField,
  setFieldsOrder,
  draftSection,
  saveWidget,
  close,
}: {
  title: string;
  onTitleChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
  metricOptions: DropdownOption[];
  selectedMetricLabel?: string;
  hasAssignmentSelected: boolean;
  showDatePeriodAlert: boolean;
  isLoadingAssignments: boolean;
  isMultiple: boolean;
  groups: EnrichedGroup[];
  onHeadingChanged: (dataType: string, heading: string) => void;
  removeField: (dataType: string, field: string) => void;
  setFieldsOrder: (dataType: string, fields: string[]) => void;
  draftSection?: PerformanceBoardTypes.SlideOut.RelatedMetricDocumentsSection;
  saveWidget: () => void;
  close: () => void;
}) => (
  <>
    <ShadowedList>
      <WidgetWrapper>
        <div style={{ marginBottom: 24 }}>
          <LabelDiv>
            <Typography.Body type="Label">Widget Title</Typography.Body>
          </LabelDiv>
          <Inputs.TextInput
            value={title}
            placeholder="Enter Title"
            onChange={onTitleChanged}
            maxLength={40}
            autoFocus
            data-testid={'title'}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <LabelDiv>
            <Typography.Body type="Label">Metric</Typography.Body>
          </LabelDiv>
          <Inputs.Dropdown
            options={metricOptions}
            selectedLabel={selectedMetricLabel}
            placeholder={
              isLoadingAssignments
                ? 'Loading metrics…'
                : 'Select a bonus metric'
            }
          />
        </div>

        {showDatePeriodAlert && (
          <div style={{ marginBottom: 24 }}>
            <WarningBanner isShort>
              <Typography.Body type="Body 12">
                This metric&apos;s date period is not &quot;To Date&quot;, so it
                cannot drive this widget. Pick a different metric or change its
                date period in Manage Bonus Metrics.
              </Typography.Body>
            </WarningBanner>
          </div>
        )}

        {hasAssignmentSelected &&
          !showDatePeriodAlert &&
          groups.map((group) => (
            <DatasetFieldsSection
              key={group.dataType}
              group={group}
              isMultiple={isMultiple}
              onHeadingChanged={onHeadingChanged}
              removeField={removeField}
              setFieldsOrder={setFieldsOrder}
            />
          ))}
      </WidgetWrapper>
    </ShadowedList>
    <WidgetFooter
      saveWidget={saveWidget}
      sectionType="Metric Widget"
      isDisabled={!draftSection}
      close={close}
    />
  </>
);

export default RelatedMetricDocumentsSlideoutWizard;
